/**
 * EmailDashboard.tsx
 *
 * Customer-facing business-email home. Drives off the
 * /api/email/subscriptions endpoint — hosting-bundle mailboxes are
 * invisible here (they belong to a different product).
 *
 * Per-subscription card shows:
 *   - domain name + tier (Email Professional / Business / Enterprise)
 *   - "Using N of M mailboxes" with a progress bar
 *   - "Add more mailboxes" button → modal that takes count, calls
 *     POST /api/email/subscriptions/:id/mailboxes/add (Stripe pro-rated
 *     immediately per industry convention).
 *   - "Cancel subscription" → confirm → POST /:id/cancel (period-end cancel)
 *   - Attention cards: provisioning stuck (no mail-domain row after 5 min),
 *     suspended (reason + billing/support link), cancelled.
 *   - v2 (mx1) extras: quick-links row (Connect devices · Mailboxes · Groups ·
 *     Delivery log · Deliverability), domain storage total from
 *     /mailboxes/usage, and a collapsed Deliverability card.
 *
 * Colour policy: logo palette only. Status conveyed by icon + text + yellow
 * intensity; never colour alone.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { MAIL_HOSTS } from '../config/mail';
import DashShell from '../components/dash/DashShell';
import { fetchUsage, MailboxUsage, sumUsage } from '../components/email/emailApi';
import { DomainUsageTotal } from '../components/email/UsageBar';
import { DeliverabilityCard } from '../components/email/DeliverabilityCard';

interface BusinessEmailSubscription {
  id: string;
  package_name: string;
  domain: string;
  status: 'pending_provisioning' | 'active' | 'suspended' | 'cancelled';
  billing_cycle: 'monthly' | 'yearly';
  base_mailboxes: number;
  extra_mailboxes: number;
  cap: number;
  base_price_monthly: string;
  extra_price_monthly: string;
  extra_price_yearly?: string | null;
  current_period_end: string | null;
  created_at: string;
  current_mailbox_count: number;
  mailbox_count?: number;
  storage_per_mailbox_gb?: number | null;
  max_aliases?: number | null;
  suspended_reason?: string | null;
  // Not in the documented payload — read defensively if the backend adds it.
  cancel_at_period_end?: boolean | null;
  // v1 path (legacy hosting-bundle mail). Present for older subs.
  customer_mail_domain_id: string | null;
  // v2 path (Business Email v2 — mx1). When set, the wizard uses /setup/v2/:id.
  mx1_mail_domain_row_id?: string | null;
}

const POLL_MS = 60_000;
const STUCK_AFTER_MS = 5 * 60 * 1000;

const token = () => localStorage.getItem('token');
async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(getApiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(init.headers || {}),
    },
  });
  const body: any = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err: any = new Error(body?.error || body?.message || `Request failed (HTTP ${r.status})`);
    err.status = r.status;
    throw err;
  }
  return body as T;
}

const num = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : fallback;
};

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

/** Close-on-Escape for modals. */
function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}

const statusChip = (s: BusinessEmailSubscription['status']) => {
  switch (s) {
    case 'active':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-500 text-primary-navy font-semibold" title="Active">✓ Active</span>;
    case 'pending_provisioning':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-primary-navy" title="Setting up">⏳ Setting up</span>;
    case 'suspended':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-200 text-primary-navy border border-primary-navy" title="Suspended">⚠ Suspended</span>;
    case 'cancelled':
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600" title="Cancelled">· Cancelled</span>;
    default:
      return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{String(s)}</span>;
  }
};

const SeatBar: React.FC<{ used: number; cap: number }> = ({ used, cap }) => {
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs text-primary-navy/80 mb-1">
        <span><strong>{used}</strong> of {cap} mailboxes used</span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded overflow-hidden" role="progressbar" aria-valuenow={used} aria-valuemin={0} aria-valuemax={cap} aria-label="Mailboxes used">
        <div className="h-full bg-secondary-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/** Reason classifier for the suspended card: billing → Billing page, otherwise → support. */
const isBillingReason = (reason: string | null | undefined) =>
  !!reason && /billing|payment|invoice|past.?due|unpaid|card|dunning|renew/i.test(reason);

const AddMailboxesModal: React.FC<{
  sub: BusinessEmailSubscription;
  onClose: () => void;
  onAdded: () => void;
}> = ({ sub, onClose, onAdded }) => {
  useEscape(onClose);
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // $/extra mailbox: monthly from extra_price_monthly; yearly from
  // extra_price_yearly when the API provides it, else the seeded 10× pattern.
  const unitMonthly = num(sub.extra_price_monthly, 3);
  const unitYearly = num(sub.extra_price_yearly, 0) > 0 ? num(sub.extra_price_yearly) : unitMonthly * 10;
  const unitPrice = sub.billing_cycle === 'yearly' ? unitYearly : unitMonthly;
  const cycleLabel = sub.billing_cycle === 'yearly' ? '/year' : '/month';
  const projected = unitPrice * count;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api(`/email/subscriptions/${sub.id}/mailboxes/add`, {
        method: 'POST',
        body: JSON.stringify({ count }),
      });
      if (!r.success) { setErr(r.error || 'Failed'); return; }
      onAdded();
    } catch (e: any) {
      setErr(e?.status === 423
        ? (e.message || 'This subscription is suspended or cancelled, so seats can\'t be added right now.')
        : (e?.message || 'Couldn\'t add mailboxes'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-primary-navy/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-mailboxes-title"
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-md w-full"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 id="add-mailboxes-title" className="text-lg font-semibold text-primary-navy">Add mailboxes</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-primary-navy/90">
            You're on <strong>{sub.package_name}</strong> with {sub.cap} mailboxes ({sub.base_mailboxes} included
            {sub.extra_mailboxes > 0 ? <> + {sub.extra_mailboxes} extra</> : null}).
          </div>
          <div>
            <label htmlFor="add-count" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">How many to add?</label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCount(c => Math.max(1, c - 1))}
                className="w-9 h-9 rounded-lg border border-gray-300 text-primary-navy hover:bg-secondary-50"
                disabled={busy || count <= 1}
                aria-label="Fewer"
              >−</button>
              <input
                id="add-count"
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={e => setCount(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                className="w-20 text-center px-2 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <button
                type="button"
                onClick={() => setCount(c => Math.min(500, c + 1))}
                className="w-9 h-9 rounded-lg border border-gray-300 text-primary-navy hover:bg-secondary-50"
                disabled={busy}
                aria-label="More"
              >+</button>
              <span className="text-sm text-gray-500 ml-2">extra mailbox{count > 1 ? 'es' : ''}</span>
            </div>
          </div>
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
            <div className="text-xs text-primary-navy/70 mb-1">New mailbox limit</div>
            <div className="text-lg font-semibold text-primary-navy">
              {sub.cap + count} mailboxes
            </div>
            <div className="text-xs text-primary-navy/80 mt-2">
              ${unitPrice.toFixed(2)} × {count} = <strong>${projected.toFixed(2)}{cycleLabel}</strong> extra
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Charged prorated to your current billing period.
            </div>
          </div>
          {err && (
            <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy">
              🚨 {err}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-primary-navy" disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy}
            className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50">
            {busy ? 'Adding…' : `Add ${count} mailbox${count > 1 ? 'es' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const CancelSubscriptionModal: React.FC<{
  sub: BusinessEmailSubscription;
  onClose: () => void;
  onDone: () => void;
}> = ({ sub, onClose, onDone }) => {
  useEscape(onClose);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const periodEnd = fmtDate(sub.current_period_end);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api(`/email/subscriptions/${sub.id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'customer_requested' }),
      });
      if (!r.success) { setErr(r.error || 'Cancellation failed'); return; }
      setResult(periodEnd
        ? `Cancellation scheduled. Mail for ${sub.domain} keeps working until ${periodEnd}, then the subscription ends.`
        : `Cancellation scheduled. Mail for ${sub.domain} keeps working until the end of the current billing period.`);
      onDone();
    } catch (e: any) {
      setErr(e?.message || 'Cancellation failed');
    } finally { setBusy(false); }
  };

  const canConfirm = confirmText.trim().toLowerCase() === sub.domain.toLowerCase();

  return (
    <div className="fixed inset-0 bg-primary-navy/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-sub-title"
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-md w-full"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 id="cancel-sub-title" className="text-lg font-semibold text-primary-navy">Cancel subscription</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4 text-sm text-primary-navy/90">
          {result ? (
            <div role="status" aria-live="polite" className="bg-secondary-50 border border-secondary-200 rounded-lg p-3">
              ✓ {result}
            </div>
          ) : (
            <>
              <p>
                This cancels <strong>{sub.package_name}</strong> for <strong>{sub.domain}</strong> at the end of the current
                billing period{periodEnd ? <> (<strong>{periodEnd}</strong>)</> : null}. Until then everything keeps working.
              </p>
              <p>
                After that date mailboxes stop receiving mail and stored messages are removed. Export anything you need first.
              </p>
              <div>
                <label htmlFor="cancel-confirm" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">
                  Type the domain to confirm
                </label>
                <input
                  id="cancel-confirm"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={sub.domain}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
                />
              </div>
              {err && (
                <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3">
                  🚨 {err}
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-primary-navy" disabled={busy}>
            {result ? 'Close' : 'Keep subscription'}
          </button>
          {!result && (
            <button type="button" onClick={submit} disabled={busy || !canConfirm}
              className="text-sm bg-primary-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {busy ? 'Cancelling…' : 'Cancel at period end'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SubCard: React.FC<{ sub: BusinessEmailSubscription; onChanged: () => void }> = ({ sub, onChanged }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [usage, setUsage] = useState<MailboxUsage[] | null>(null);
  const [deliverabilityOpen, setDeliverabilityOpen] = useState(false);

  const hasV2 = !!sub.mx1_mail_domain_row_id;
  const v2Id = sub.mx1_mail_domain_row_id || null;

  // Domain storage total — v2 only, once per card; advisory so failures
  // just hide the bar.
  useEffect(() => {
    if (!v2Id || sub.status !== 'active') return;
    let alive = true;
    fetchUsage(v2Id).then(rows => { if (alive) setUsage(Array.isArray(rows) ? rows : []); }).catch(() => { /* hidden */ });
    return () => { alive = false; };
  }, [v2Id, sub.status, sub.current_mailbox_count]);
  const totals = sumUsage(usage);
  const hasV1 = !!sub.customer_mail_domain_id;
  const hasRow = hasV2 || hasV1;
  const ageMs = Date.now() - new Date(sub.created_at).getTime();
  const stuck = sub.status === 'pending_provisioning' && !hasRow && Number.isFinite(ageMs) && ageMs > STUCK_AFTER_MS;
  const used = num(sub.current_mailbox_count ?? sub.mailbox_count);
  const renews = fmtDate(sub.current_period_end);
  const billingReason = isBillingReason(sub.suspended_reason);

  const mailboxesPath = hasV2
    ? `/dashboard/email/mailboxes/v2/${sub.mx1_mail_domain_row_id}`
    : `/dashboard/email/mailboxes/${sub.customer_mail_domain_id}`;
  const setupPath = hasV2
    ? `/dashboard/email/onboard/${sub.mx1_mail_domain_row_id}`
    : `/dashboard/email/setup/${sub.customer_mail_domain_id}`;
  // v2 gets the device-guides page; v1 keeps the setup page's settings block.
  const settingsPath = hasV2
    ? `/dashboard/email/connect/v2/${sub.mx1_mail_domain_row_id}`
    : `/dashboard/email/setup/${sub.customer_mail_domain_id}`;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-primary-navy break-all">{sub.domain}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {sub.package_name} · {sub.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}
            {renews
              ? (sub.status === 'cancelled' || sub.cancel_at_period_end
                  ? <> · ends {renews}</>
                  : <> · renews {renews}</>)
              : null}
            {sub.storage_per_mailbox_gb ? <> · {sub.storage_per_mailbox_gb} GB per mailbox</> : null}
          </p>
        </div>
        {statusChip(sub.status)}
      </div>

      {sub.cancel_at_period_end && sub.status !== 'cancelled' && (
        <div role="status" className="mb-3 text-sm text-primary-navy bg-secondary-50 border border-secondary-200 rounded-lg p-3">
          · Cancellation scheduled — service continues until {renews || 'the end of the current period'}.
        </div>
      )}

      {sub.status === 'active' && hasRow && (
        <div className="space-y-3 mt-4">
          <SeatBar used={used} cap={sub.cap} />
          <div className="flex flex-wrap gap-2">
            <Link
              to={mailboxesPath}
              className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition"
            >
              Manage mailboxes
            </Link>
            {!sub.cancel_at_period_end && (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="text-sm bg-secondary-500 text-primary-navy font-medium px-3 py-1.5 rounded-lg hover:bg-secondary-600 transition"
              >
                + Add mailboxes
              </button>
            )}
            <a
              href={MAIL_HOSTS.webmail}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm bg-white border border-gray-300 text-primary-navy px-3 py-1.5 rounded-lg hover:bg-gray-50 transition"
            >
              Open webmail
            </a>
            <Link
              to={settingsPath}
              className="text-sm bg-white border border-gray-300 text-primary-navy px-3 py-1.5 rounded-lg hover:bg-gray-50 transition"
            >
              {hasV2 ? 'Connect devices' : 'Connection settings'}
            </Link>
            {!sub.cancel_at_period_end && (
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                className="text-sm text-gray-500 hover:text-primary-navy underline px-2 py-1.5 ml-auto"
              >
                Cancel subscription
              </button>
            )}
          </div>

          {hasV2 && v2Id && (
            <>
              <nav aria-label={`Quick links for ${sub.domain}`} className="flex flex-wrap gap-x-3 gap-y-1 text-sm pt-1">
                <Link to={`/dashboard/email/connect/v2/${v2Id}`} className="text-primary-navy hover:underline">Connect devices</Link>
                <span aria-hidden className="text-gray-300">·</span>
                <Link to={`${mailboxesPath}#mailboxes`} className="text-primary-navy hover:underline">Mailboxes</Link>
                <span aria-hidden className="text-gray-300">·</span>
                <Link to={`${mailboxesPath}#groups`} className="text-primary-navy hover:underline">Groups</Link>
                <span aria-hidden className="text-gray-300">·</span>
                <Link to={`${mailboxesPath}#delivery`} className="text-primary-navy hover:underline">Delivery log</Link>
                <span aria-hidden className="text-gray-300">·</span>
                <button type="button" onClick={() => setDeliverabilityOpen(true)} className="text-primary-navy hover:underline">Deliverability</button>
              </nav>

              {usage && usage.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-4">
                  <DomainUsageTotal usedMb={totals.used} quotaMb={totals.quota} mailboxes={usage.length} messages={totals.messages} compact />
                </div>
              )}

              <DeliverabilityCard key={deliverabilityOpen ? 'open' : 'closed'} domainRowId={v2Id} defaultOpen={deliverabilityOpen} />
            </>
          )}
        </div>
      )}

      {sub.status === 'active' && !hasRow && (
        <div role="status" className="mt-4 text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-3">
          ⚠ <strong>Setup needs attention.</strong> This subscription is active but has no mail domain attached.{' '}
          <Link to="/contact" className="underline">Contact support</Link> and quote <span className="font-mono">{sub.domain}</span>.
        </div>
      )}

      {sub.status === 'pending_provisioning' && hasRow && (
        <div className="space-y-3 mt-4">
          <div className="text-sm text-primary-navy/80">
            We're setting up your Business Email. The onboarding wizard walks you through
            DNS, mailboxes, and (if you're moving from another provider) migration.
          </div>
          <Link
            to={setupPath}
            className="text-sm bg-secondary-500 text-primary-navy font-medium px-3 py-1.5 rounded-lg hover:bg-secondary-600 transition inline-block"
          >
            {hasV2 ? 'Continue onboarding →' : 'Continue setup →'}
          </Link>
        </div>
      )}

      {sub.status === 'pending_provisioning' && !hasRow && !stuck && (
        <div role="status" className="mt-4 text-sm text-primary-navy/70">
          ⏳ Provisioning hasn't started yet — this page refreshes automatically. Give it a minute.
        </div>
      )}

      {stuck && (
        <div role="alert" className="mt-4 text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-3">
          🚨 <strong>Setup needs attention — contact support.</strong> Your payment went through, but provisioning
          hasn't started after several minutes. Our team has been alerted; you can also{' '}
          <Link to="/contact" className="underline">contact support</Link> and quote <span className="font-mono">{sub.domain}</span>.
          You won't be charged again.
        </div>
      )}

      {sub.status === 'suspended' && (
        <div role="alert" className="mt-4 text-sm text-primary-navy bg-secondary-200 border border-primary-navy rounded-lg p-3 space-y-2">
          <div>
            ⚠ <strong>This subscription is suspended.</strong>{' '}
            {sub.suspended_reason
              ? <>Reason: {sub.suspended_reason}</>
              : 'Mail for this domain is paused until it\'s resolved.'}
          </div>
          <div className="flex flex-wrap gap-2">
            {billingReason ? (
              <Link to="/dashboard/billing" className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">
                Fix billing →
              </Link>
            ) : (
              <Link to="/contact" className="text-sm bg-primary-navy text-white px-3 py-1.5 rounded-lg hover:bg-primary-700">
                Contact support →
              </Link>
            )}
            {billingReason && (
              <Link to="/contact" className="text-sm border border-primary-navy text-primary-navy px-3 py-1.5 rounded-lg hover:bg-secondary-50">
                Contact support
              </Link>
            )}
            {hasRow && (
              <Link to={mailboxesPath} className="text-sm border border-gray-300 text-primary-navy px-3 py-1.5 rounded-lg hover:bg-gray-50">
                View mailboxes
              </Link>
            )}
          </div>
        </div>
      )}

      {sub.status === 'cancelled' && (
        <div role="status" className="mt-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
          · This subscription is cancelled{renews ? <> and ends on {renews}</> : null}. Want email on this domain again?{' '}
          <Link to="/email" className="underline text-primary-navy">Browse plans</Link>.
        </div>
      )}

      {addOpen && (
        <AddMailboxesModal
          sub={sub}
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); onChanged(); }}
        />
      )}
      {cancelOpen && (
        <CancelSubscriptionModal
          sub={sub}
          onClose={() => setCancelOpen(false)}
          onDone={onChanged}
        />
      )}
    </div>
  );
};

const EmailDashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [subs, setSubs] = useState<BusinessEmailSubscription[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ success: boolean; data: BusinessEmailSubscription[]; error?: string }>('/email/subscriptions');
      if (!r.success) { setError(r.error || 'Failed to load'); return; }
      setSubs(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {
    if (!user) return;
    // Light auto-refresh so status + seat counts stay current. Skips ticks
    // while the tab is hidden so a backgrounded dashboard doesn't poll.
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      load();
    }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, load]);

  if (authLoading || (loading && !subs && !error)) {
    return (
      <DashShell title="Business Email" subtitle="Loading your mailboxes…">
        <div className="h-28 rounded-2xl bg-white/60 backdrop-blur animate-pulse mb-6" />
        <div className="h-40 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }
  if (!user) {
    return (
      <DashShell title="Business Email" subtitle="Sign in required">
        <div className="rounded-2xl bg-white/80 backdrop-blur border border-white/40 p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Please log in</h2>
          <p className="text-slate-600">You need to be signed in to manage your business email.</p>
          <Link to="/login?return=%2Fcustomer%2Femail" className="mt-4 inline-block text-sm bg-primary-navy text-white px-4 py-2 rounded-lg">Sign in</Link>
        </div>
      </DashShell>
    );
  }

  return (
    <DashShell
      title="Business Email"
      subtitle="Your custom-domain mailboxes. Add seats any time — billed prorated."
    >
      <div className="space-y-6">
        {error && (
          <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-xl p-4 text-primary-navy">
            <strong>Couldn't load:</strong> {error}{' '}
            <button type="button" onClick={load} className="underline text-sm ml-2">Retry</button>
          </div>
        )}

        {subs && subs.length === 0 && (
          <div className="bg-white/80 backdrop-blur border border-white/40 rounded-2xl p-8 text-center shadow-sm">
            <div className="text-4xl mb-3" aria-hidden>✉️</div>
            <h2 className="text-lg font-semibold text-slate-900">No business-email subscription yet</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
              Pick a plan to get professional email on your own domain — Professional, Business, or Enterprise.
              Hosting customers: your hosting plan already includes mailboxes, manage them from your hosting dashboard.
            </p>
            <Link
              to="/email"
              className="mt-4 inline-block bg-secondary-500 text-primary-navy font-medium px-5 py-2 rounded-lg hover:bg-secondary-600 transition"
            >
              Browse email plans →
            </Link>
          </div>
        )}

        {subs && subs.length > 0 && (
          <div className="space-y-4">
            {subs.map(s => <SubCard key={s.id} sub={s} onChanged={load} />)}
          </div>
        )}
      </div>
    </DashShell>
  );
};

export default EmailDashboard;
